/**
 * @file OperationsSignalCoveragePage.jsx — OPERATIONS department — Signal Coverage.
 * @description Answers one question per machine: is it actually sending its
 *              engine data, its angles, its cage load? The Errors page shows
 *              what the machine COMPLAINED about and the data-quality alarm
 *              fires only when CAN stops altogether — neither notices one
 *              signal quietly going missing, which is how a dead sensor hides
 *              for months.
 *
 *              Three verdicts per signal, because present/absent is not enough:
 *                In range   — arrived with a believable value
 *                Suspect    — arrived, but only ever as an impossible one (a
 *                             J1939 no-data sentinel like coolant -40). The
 *                             wire is alive; the sensor is not.
 *                Not sent   — never appeared in the window at all
 *
 * This file belongs to the OPERATIONS department. Departments never share page
 * files: a change for OPERATIONS is made HERE and cannot leak into another
 * department's screens. Backend: GET /api/devices/signal-coverage, which
 * borrows its sanity windows from the alarm engine's own thresholds so the two
 * can never disagree about what an impossible reading is.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Card, Stack, Chip, Button, IconButton, Tooltip, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Skeleton, ToggleButton, ToggleButtonGroup, Collapse, TextField, InputAdornment,
} from '@mui/material';
import {
    Refresh as RefreshIcon, Search as SearchIcon,
    KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon,
    CheckCircle as OkIcon, ReportProblem as SuspectIcon, RemoveCircleOutline as MissingIcon,
    ChevronRight as OpenIcon,
} from '@mui/icons-material';
import mewpService from '../../../services/mewpService';
import { formatToIST } from '../../../utils/dataUtils';

// Colour never travels alone — each verdict carries the word for what it means.
const VERDICT = {
    ok:      { color: 'success', label: 'In range', Icon: OkIcon },
    suspect: { color: 'warning', label: 'Suspect',  Icon: SuspectIcon },
    missing: { color: 'default', label: 'Not sent', Icon: MissingIcon },
};

const groupTone = (g) => (g.status === 'ok' ? 'success' : g.status === 'missing' ? 'error' : 'warning');

const MachineRow = ({ machine, groups, onOpen }) => {
    const [open, setOpen] = useState(false);
    const silent = machine.frames === 0;

    return (
        <>
            <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell sx={{ width: 40 }}>
                    <IconButton size="small" onClick={() => setOpen(o => !o)} aria-label={open ? 'Hide signals' : 'Show signals'}>
                        {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight={800}>{machine.equipment_id}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {machine.model_number || '—'}{machine.source === 'sinoboom' ? ' · Sinoboom' : ''}
                    </Typography>
                </TableCell>
                <TableCell>
                    {silent ? (
                        <Chip size="small" color="error" label="No frames" sx={{ fontWeight: 700, height: 22, fontSize: '11px' }} />
                    ) : (
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {formatToIST(machine.last_frame_at, 'dd MMM, hh:mm a')}
                            <br />{machine.frames} frames
                        </Typography>
                    )}
                </TableCell>
                {groups.map(g => {
                    const mg = machine.groups.find(x => x.key === g.key);
                    return (
                        <TableCell key={g.key} align="center">
                            <Chip
                                size="small"
                                color={groupTone(mg)}
                                variant={mg.status === 'ok' ? 'filled' : 'outlined'}
                                label={`${mg.ok}/${mg.total}`}
                                sx={{ fontWeight: 800, height: 22, fontSize: '11px', minWidth: 46 }}
                            />
                        </TableCell>
                    );
                })}
                <TableCell align="right">
                    <Tooltip title="Open this machine">
                        <IconButton size="small" onClick={() => onOpen(machine.equipment_id)}><OpenIcon fontSize="small" /></IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>

            <TableRow>
                <TableCell sx={{ py: 0, borderBottom: open ? 1 : 0, borderColor: 'divider' }} colSpan={groups.length + 4}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {machine.groups.map(g => (
                                <Box key={g.key} sx={{ minWidth: 220 }}>
                                    <Typography variant="caption" fontWeight={800} color="text.secondary"
                                        sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                        {g.label}
                                    </Typography>
                                    {g.signals.map(s => {
                                        const v = VERDICT[s.status] || VERDICT.missing;
                                        return (
                                            <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
                                                <v.Icon
                                                    sx={{ fontSize: 15, color: v.color === 'default' ? 'text.disabled' : `${v.color}.main` }}
                                                />
                                                <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>{s.label}</Typography>
                                                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                                                    {s.value == null ? '—' : s.value}
                                                </Typography>
                                                <Typography variant="caption"
                                                    sx={{ color: v.color === 'default' ? 'text.disabled' : `${v.color}.main`, fontWeight: 700, width: 62, textAlign: 'right' }}>
                                                    {v.label}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ))}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const OperationsSignalCoveragePage = () => {
    const navigate = useNavigate();
    const [hours, setHours] = useState(24);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [q, setQ] = useState('');

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await mewpService.getSignalCoverage({ hours });
            setData(res || null);
        } catch (e) {
            console.error('Signal coverage fetch failed:', e);
            setError(e.message || 'Could not load signal coverage');
        } finally {
            setLoading(false);
        }
    }, [hours]);

    useEffect(() => { load(); }, [load]);

    const groups = data?.groups || [];
    const machines = useMemo(() => {
        const rows = data?.data || [];
        const needle = q.trim().toLowerCase();
        const filtered = needle
            ? rows.filter(m => `${m.equipment_id} ${m.model_number || ''}`.toLowerCase().includes(needle))
            : rows;
        // Worst first — the reason to open this page is at the top of it.
        return [...filtered].sort((a, b) =>
            (a.frames === 0 ? -1 : 0) - (b.frames === 0 ? -1 : 0)
            || (a.received / a.expected) - (b.received / b.expected));
    }, [data, q]);

    const s = data?.summary;

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {s && (
                        <>
                            <Chip size="small" label={`${s.machines} machines`} sx={{ fontWeight: 700 }} />
                            <Chip size="small" color="success" label={`${s.fullyReporting} sending everything`} sx={{ fontWeight: 700 }} />
                            <Chip size="small" color="warning" label={`${s.anySuspect} with suspect readings`} sx={{ fontWeight: 700 }} />
                            <Chip size="small" color="error" label={`${s.silent} sent nothing`} sx={{ fontWeight: 700 }} />
                        </>
                    )}
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <TextField
                        size="small" placeholder="Search machine…" value={q} onChange={e => setQ(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        sx={{ width: 200 }}
                    />
                    <ToggleButtonGroup size="small" exclusive value={hours} onChange={(_e, v) => v && setHours(v)}>
                        <ToggleButton value={24}>24h</ToggleButton>
                        <ToggleButton value={72}>3d</ToggleButton>
                        <ToggleButton value={168}>7d</ToggleButton>
                    </ToggleButtonGroup>
                    <Button size="small" variant="contained" startIcon={<RefreshIcon />} onClick={load}
                        sx={{ textTransform: 'none', fontWeight: 700 }}>
                        Refresh
                    </Button>
                </Stack>
            </Stack>

            {error && <Alert severity="error" sx={{ borderRadius: '16px', mb: 2 }}>{error}</Alert>}

            <Card>
                {loading ? (
                    <Box p={3}>{[...Array(6)].map((_, i) => <Skeleton key={i} height={44} />)}</Box>
                ) : machines.length === 0 ? (
                    <Box p={6} textAlign="center">
                        <Typography variant="body2" color="text.secondary" fontWeight={700}>
                            {q ? 'No machine matches that search' : 'No working-fleet machines to report on'}
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    <TableCell sx={{ fontWeight: 700, fontSize: '10px', color: 'text.secondary' }}>MACHINE</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '10px', color: 'text.secondary' }}>LAST FRAME</TableCell>
                                    {groups.map(g => (
                                        <TableCell key={g.key} align="center"
                                            sx={{ fontWeight: 700, fontSize: '10px', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                            {g.label.toUpperCase()}
                                        </TableCell>
                                    ))}
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {machines.map(m => (
                                    <MachineRow
                                        key={m.equipment_id}
                                        machine={m}
                                        groups={groups}
                                        onOpen={(id) => navigate(`/mewp-detail/${encodeURIComponent(id)}`)}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Card>
        </Box>
    );
};

export default OperationsSignalCoveragePage;
